import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ClientIInfo } from '../../../../../core/models/parking.model';
import {
  AdvanceQuotaOption,
  AdvanceVehicleTypeOption,
  QuotaCardAdvanceDetail,
} from '../../../../../core/models/card-quota/quota-card-advance.model';
import { ClientQuotaListRow } from '../../../../../core/models/card-quota/client-quota.model';
import { MapperQuotaCardAdvanceService } from '../../../../../core/mappers/card-quota/mapper-quota-card-advance.service';
import { MapperClientQuotaService } from '../../../../../core/mappers/card-quota/mapper-client-quota.service';
import { ApiCardQuotaService } from '../../../../../core/services/api/card-quota/api-card-quota.service';
import { ClientAccessScopeService } from '../../../../../core/services/client-access-scope.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import {
  PssSearchablePickerComponent,
  SearchablePickerOption,
} from '../../../../../tools/template/pss/pss-searchable-picker/pss-searchable-picker.component';
import { isDateRangeValid } from '../../../../../core/utils/date-input.util';

@Component({
  selector: 'app-modal-quota-card-advance',
  standalone: true,
  imports: [CommonModule, FormsModule, PssSearchablePickerComponent],
  templateUrl: './modal-quota-card-advance.component.html',
  styleUrl: './modal-quota-card-advance.component.css',
})
export class ModalQuotaCardAdvanceComponent implements OnInit {
  private static readonly PREVIEW_SIZE = 20;

  @Input() advanceId = 0;
  @Input() siteId = 0;
  @Input() initialClientId = 0;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  formData!: QuotaCardAdvanceDetail;
  clients: ClientIInfo[] = [];
  vehicleTypes: AdvanceVehicleTypeOption[] = [];
  quotaSummaryItems: ClientQuotaListRow[] = [];
  selectedQuotaRow: ClientQuotaListRow | null = null;
  resolvedCard: AdvanceQuotaOption | null = null;
  isSaving = signal(false);
  isLoadingDetail = signal(false);
  isLoadingQuotaSummary = signal(false);
  isResolvingCard = signal(false);

  clientPickerOptions = signal<SearchablePickerOption[]>([]);
  clientPickerTotal = signal(0);
  private clientPickerSearch = '';

  private readonly api = inject(ApiCardQuotaService);
  private readonly clientScope = inject(ClientAccessScopeService);
  private readonly mapper = inject(MapperQuotaCardAdvanceService);
  private readonly clientQuotaMapper = inject(MapperClientQuotaService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isClientFieldLocked(): boolean {
    return this.clientScope.isClientLocked();
  }

  get maxQty(): number {
    return this.selectedQuotaRow?.quotaRemaining ?? 0;
  }

  get canEditQty(): boolean {
    return !!this.selectedQuotaRow && this.formData.vehicleTypeId > 0 && this.maxQty > 0;
  }

  get qtyExceedsQuota(): boolean {
    return this.canEditQty && this.formData.qty > this.maxQty;
  }

  get isQtyValid(): boolean {
    if (!this.selectedQuotaRow || this.formData.vehicleTypeId <= 0) return false;
    if (this.maxQty <= 0) return false;
    return this.formData.qty >= 1 && this.formData.qty <= this.maxQty;
  }

  get canSave(): boolean {
    if (this.isSaving() || this.isResolvingCard()) return false;
    if (!this.formData.clientId || !this.formData.quotaId || !this.formData.vehicleTypeId) return false;
    if (!this.resolvedCard) return false;
    if (!this.isQtyValid) return false;
    return isDateRangeValid(this.formData.startDate, this.formData.endDate);
  }

  get requiresClientFirst(): boolean {
    return !this.formData.clientId;
  }

  get canShowQuotaList(): boolean {
    return this.formData.clientId > 0 && (this.isLoadingQuotaSummary() || this.quotaSummaryItems.length > 0);
  }

  get hasUsableQuotaRemaining(): boolean {
    return this.quotaSummaryItems.some((q) => q.quotaRemaining > 0);
  }

  get totalQuotaRemaining(): number {
    return this.quotaSummaryItems.reduce((sum, q) => sum + q.quotaRemaining, 0);
  }

  get canPickVehicleType(): boolean {
    return !!this.selectedQuotaRow && this.selectedQuotaRow.quotaRemaining > 0;
  }

  get canEditQtyAndDates(): boolean {
    return this.canEditQty;
  }

  quotaLevelLabel = (level: string) => this.clientQuotaMapper.quotaLevelLabel(level);

  isQuotaSelected(q: ClientQuotaListRow): boolean {
    return this.selectedQuotaRow?.id === q.id;
  }

  ngOnInit(): void {
    this.formData = this.mapper.createEmptyForm(this.siteId);
    this.loadClients();
    if (this.advanceId > 0) {
      this.loadDetail(this.advanceId);
    } else if (this.initialClientId > 0) {
      this.formData.clientId = this.initialClientId;
      this.onClientSelected(this.initialClientId);
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onSaveDraft(): void {
    this.save(false);
  }

  onSubmitApproval(): void {
    this.save(true);
  }

  onClientPickerOpened(): void {
    this.clientPickerSearch = '';
    this.loadClientPickerOptions('');
  }

  onClientPickerSearch(text: string): void {
    this.clientPickerSearch = text;
    this.loadClientPickerOptions(text);
  }

  onClientPickerSelected(option: SearchablePickerOption): void {
    this.onClientSelected(option.id, option.label);
  }

  onClientPickerCleared(): void {
    this.formData.clientId = 0;
    this.formData.clientName = '';
    this.resetAfterClient();
  }

  onQuotaFromListSelected(q: ClientQuotaListRow): void {
    if (q.quotaRemaining <= 0) {
      this.toast.error('โควต้านี้คงเหลือไม่พอ');
      return;
    }
    this.selectedQuotaRow = q;
    this.formData.quotaId = q.id;
    this.formData.quotaName = q.name;
    this.formData.quotaRemaining = q.quotaRemaining;
    this.formData.vehicleTypeId = 0;
    this.formData.vehicleTypeName = '';
    this.resolvedCard = null;
    this.formData.cardTypeId = 0;
    this.formData.cardTypeName = '';
    this.formData.subscriptionId = 0;
    this.formData.subscriptionName = '';
    this.vehicleTypes = [];
    if (this.formData.qty > q.quotaRemaining) {
      this.formData.qty = q.quotaRemaining;
    } else if (this.formData.qty < 1) {
      this.formData.qty = 1;
    }
    this.clampQty();
    this.loadVehicleTypes();
  }

  onVehicleTypeChange(): void {
    this.resolvedCard = null;
    this.formData.cardTypeId = 0;
    this.formData.cardTypeName = '';
    this.formData.subscriptionId = 0;
    this.formData.subscriptionName = '';
    if (this.formData.vehicleTypeId > 0) {
      const vt = this.vehicleTypes.find((v) => v.id === this.formData.vehicleTypeId);
      this.formData.vehicleTypeName = vt?.name ?? '';
      this.enrichCardInfoFromVehicleType();
    }
  }

  onQtyChange(): void {
    this.clampQty();
  }

  onQtyInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    if (raw === '' || raw === '-') return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    if (this.maxQty > 0 && parsed > this.maxQty) {
      this.formData.qty = this.maxQty;
      (event.target as HTMLInputElement).value = String(this.maxQty);
    }
  }

  private onClientSelected(clientId: number, clientName?: string): void {
    this.formData.clientId = clientId;
    const client = this.clients.find((c) => c.id === clientId);
    this.formData.clientName = clientName ?? client?.name ?? '';
    this.resetAfterClient();
    this.loadQuotaSummary();
  }

  private resetAfterClient(): void {
    this.quotaSummaryItems = [];
    this.vehicleTypes = [];
    this.selectedQuotaRow = null;
    this.resolvedCard = null;
    this.formData.vehicleTypeId = 0;
    this.formData.vehicleTypeName = '';
    this.formData.quotaId = null;
    this.formData.quotaName = '';
    this.formData.quotaRemaining = 0;
    this.formData.cardTypeId = 0;
    this.formData.cardTypeName = '';
    this.formData.subscriptionId = 0;
    this.formData.subscriptionName = '';
  }

  private loadQuotaSummary(restoreSelection = false): void {
    if (!this.formData.clientId) return;

    this.isLoadingQuotaSummary.set(true);
    this.api
      .getClientQuotasForTable(this.siteId, 1, 100, this.formData.clientId, null, 'active')
      .subscribe({
        next: (res) => {
          const mapped = this.clientQuotaMapper.mapTable(res);
          this.quotaSummaryItems = mapped.items;
          this.isLoadingQuotaSummary.set(false);

          if (this.quotaSummaryItems.length === 0) {
            this.toast.error('ไม่พบโควต้า active สำหรับร้านนี้');
            return;
          }

          if (!this.hasUsableQuotaRemaining) {
            this.toast.error('โควต้าคงเหลือไม่พอ — ไม่สามารถสร้าง Advance ได้');
          }

          if (restoreSelection && this.formData.quotaId) {
            const q = this.quotaSummaryItems.find((x) => x.id === this.formData.quotaId);
            if (q && q.quotaRemaining > 0) {
              this.selectedQuotaRow = q;
              this.formData.quotaName = q.name;
              this.formData.quotaRemaining = q.quotaRemaining;
              this.loadVehicleTypes(true);
            }
          }
        },
        error: () => {
          this.quotaSummaryItems = [];
          this.isLoadingQuotaSummary.set(false);
          this.toast.error('โหลดสรุปโควต้าล้มเหลว');
        },
      });
  }

  private enrichCardInfoFromVehicleType(): void {
    if (!this.formData.clientId || !this.formData.quotaId || this.formData.vehicleTypeId <= 0) {
      return;
    }

    this.isResolvingCard.set(true);
    this.api
      .getAdvanceQuotasForClient(this.siteId, this.formData.clientId, this.formData.vehicleTypeId, 1, 100)
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapAdvanceQuotas(res);
          const quota = mapped.items.find((q) => q.id === this.formData.quotaId);
          if (quota) {
            this.resolvedCard = quota;
            this.formData.cardTypeId = quota.cardTypeId;
            this.formData.cardTypeName = quota.cardTypeName;
            this.formData.subscriptionId = quota.subscriptionId;
            this.formData.subscriptionName = quota.subscriptionName;
          } else {
            this.resolvedCard = null;
            this.formData.cardTypeId = 0;
            this.formData.cardTypeName = '';
            this.formData.subscriptionId = 0;
            this.formData.subscriptionName = '';
            this.toast.error('ไม่พบข้อมูลประเภทบัตรสำหรับโควต้าและประเภทรถที่เลือก');
          }
          this.isResolvingCard.set(false);
        },
        error: () => {
          this.isResolvingCard.set(false);
          this.toast.error('โหลดประเภทบัตรจาก whitelist ล้มเหลว');
        },
      });
  }

  private clampQty(): void {
    const max = this.maxQty;
    if (max <= 0) {
      this.formData.qty = 0;
      return;
    }
    if (this.formData.qty > max) {
      this.formData.qty = max;
    }
    if (this.formData.qty < 1) {
      this.formData.qty = 1;
    }
  }

  private save(submitForApproval: boolean): void {
    const isEdit = this.advanceId > 0;
    if (isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    if (!this.formData.clientId) {
      this.toast.error('กรุณาเลือกร้าน');
      return;
    }
    if (!this.formData.quotaId) {
      this.toast.error('กรุณาเลือกโควต้าจากรายการ');
      return;
    }
    if (!this.formData.vehicleTypeId) {
      this.toast.error('กรุณาเลือกประเภทรถ');
      return;
    }
    if (!this.resolvedCard) {
      this.toast.error('ไม่พบประเภทบัตรจาก whitelist — กรุณาเลือกประเภทรถใหม่');
      return;
    }
    if (!this.isQtyValid) {
      if (this.maxQty <= 0) {
        this.toast.error('โควต้าคงเหลือไม่พอ — ไม่สามารถสร้างรหัสได้');
      } else {
        this.toast.error(`จำนวนต้องอยู่ระหว่าง 1 ถึง ${this.maxQty} (โควต้าคงเหลือ)`);
      }
      return;
    }
    if (!isDateRangeValid(this.formData.startDate, this.formData.endDate)) {
      this.toast.error('วันหมดอายุต้องไม่น้อยกว่าวันเริ่มต้น');
      return;
    }

    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url);
    const payload = this.mapper.buildSavePayload(this.formData, userId, menuId, submitForApproval);

    this.isSaving.set(true);
    this.api.saveQuotaCardAdvance(payload).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        const requiresApproval = Boolean(
          res.requiresApproval ?? (res as unknown as Record<string, unknown>)['RequiresApproval']
        );
        if (submitForApproval) {
          this.toast.success(requiresApproval ? 'ส่งอนุมัติสำเร็จ' : 'อนุมัติอัตโนมัติ — พร้อม Gen Code');
        } else {
          this.toast.success('บันทึก draft สำเร็จ');
        }
        this.saveSuccess.emit();
        this.onClose();
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'บันทึกล้มเหลว');
      },
    });
  }

  private loadDetail(id: number): void {
    this.isLoadingDetail.set(true);
    this.api.getQuotaCardAdvanceById(id).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapDetail(res as QuotaCardAdvanceDetail & Record<string, unknown>);
        this.loadQuotaSummary(true);
        this.isLoadingDetail.set(false);
      },
      error: () => {
        this.isLoadingDetail.set(false);
        this.toast.error('โหลดรายละเอียดล้มเหลว');
      },
    });
  }

  private loadClients(): void {
    const userId = Number(this.cookieService.get('user_id') || 0);
    if (!this.siteId || !userId) {
      this.clients = [];
      return;
    }

    this.clientScope.load(this.siteId, userId).subscribe({
      next: (state) => {
        this.clients = state.clients;
        if (this.formData.clientId > 0 && this.advanceId === 0) {
          this.onClientSelected(this.formData.clientId);
        } else if (this.isClientFieldLocked && this.clients.length === 1 && this.advanceId === 0) {
          this.onClientSelected(this.clients[0].id);
        }
        this.loadClientPickerOptions('');
      },
      error: () => {
        this.clients = [];
      },
    });
  }

  private loadVehicleTypes(restoreVehicleType = false): void {
    if (!this.formData.clientId || !this.selectedQuotaRow) return;
    this.api.getAdvanceVehicleTypesForClient(this.siteId, this.formData.clientId).subscribe({
      next: (res) => {
        this.vehicleTypes = this.mapper.mapVehicleTypes(res ?? []);
        if (restoreVehicleType && this.formData.vehicleTypeId > 0) {
          this.enrichCardInfoFromVehicleType();
        }
      },
      error: () => {
        this.vehicleTypes = [];
      },
    });
  }

  private loadClientPickerOptions(searchText: string): void {
    const sorted = [...this.clients].sort((a, b) => a.name.localeCompare(b.name, 'th'));
    const q = searchText.trim().toLowerCase();
    const filtered = q ? sorted.filter((c) => c.name.toLowerCase().includes(q)) : sorted;
    this.clientPickerTotal.set(filtered.length);
    this.clientPickerOptions.set(
      filtered.slice(0, ModalQuotaCardAdvanceComponent.PREVIEW_SIZE).map((c) => ({
        id: c.id,
        label: c.name,
      }))
    );
  }
}
